import GlueFrame from 'glueframe';
import { DateFormatter } from '../../molecules/time-formatter/time-formatter';
import BaseBehavior from '../../../framework/base/base';
import { findInstance } from '../../../shared/utilities/find-instance';
import { showElement } from '../../../shared/utilities/show-hide';
import { createAndAppendElement } from '../../../shared/utilities/element';
import createGlueframeTracking from '../../../shared/utilities/video-glueframe-tracking';

const classes = {
	loading: 'v2-video-component__content--loading',
	loaded: 'v2-video-component__content--loaded'
};

let observer;
const observerQueue = [];

// For tracking video in glue frame, add video-glueframe-tracking.js utility here and follow steps, we followed in lightbox.js

export default class VideoComponent extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();

		this.isIntersecting = null;
		if (this.dom.video) {
			this.provider = 'tencent';
			this.lazyLoaded = this.dom.video.dataset.lazyLoaded;
			if (!this.lazyLoaded) {
				VideoComponent.loadTCPlayer(this);
			}
		} else if (this.dom.iframe) {
			this.iframeLoaded = !!this.dom.iframe.dataset.loaded;
			this.lazyLoaded = this.dom.iframe.hasAttribute('data-src');
			const src = this.dom.iframe.dataset.src || this.dom.iframe.src;
			this.provider = 'video23';
			if (src.match(/\/\/(www\.|)youtu/)) {
				this.provider = 'youtube';
				if (!this.lazyLoaded) {
					VideoComponent.loadYouTubeIframeApi(this);
				}
			} else {
				this.video23Player = new GlueFrame(this.dom.iframe, 'Player');
				this.listenToPlayer = createGlueframeTracking();
				this.listenToPlayer(this.video23Player);
			}
		}

		this.rssUrl = element.dataset.rssUrl;
		if (this.rssUrl) {
			this.lazyLoaded = false;
			this.loadRssUrl();
		}
		this.addEventListeners();
	}

	static loadTCPlayer(instance) {
		if (VideoComponent.tcApiReady) {
			instance.initTCPlayer();
		}
		if (!VideoComponent.tcInitQueue) {
			VideoComponent.tcInitQueue = [];
		}
		VideoComponent.tcInitQueue.push(instance);
		if (VideoComponent.tcIframeApiLoading) {
			return;
		}
		VideoComponent.tcApiLoading = true;
		createAndAppendElement(document.body, 'link', {
			rel: 'stylesheet',
			href: '//imgcache.qq.com/open/qcloud/video/tcplayer/tcplayer.css'
		});
		createAndAppendElement(document.body, 'script', {
			src: '//imgcache.qq.com/open/qcloud/video/tcplayer/lib/hls.min.0.8.8.js'
		});
		createAndAppendElement(
			document.body,
			'script',
			{ src: '//imgcache.qq.com/open/qcloud/video/tcplayer/tcplayer.min.js' },
			{ load: VideoComponent.onTCPlayerLoad }
		);
	}

	static onTCPlayerLoad() {
		VideoComponent.tcApiReady = true;
		let instance;
		while ((instance = VideoComponent.tcInitQueue.shift())) {
			instance.initTCPlayer();
		}
	}

	static loadYouTubeIframeApi(instance) {
		if (VideoComponent.youTubeIframeApiReady) {
			instance.initYtPlayer();
		}
		if (!VideoComponent.youTubeInitQueue) {
			VideoComponent.youTubeInitQueue = [];
		}
		VideoComponent.youTubeInitQueue.push(instance);
		if (VideoComponent.youTubeIframeApiLoading) {
			return;
		}
		VideoComponent.youTubeIframeApiLoading = true;
		window.onYouTubeIframeAPIReady = VideoComponent.onYouTubeIframeAPIReady;
		createAndAppendElement(
			document.body,
			'script',
			{ src: '//www.youtube.com/iframe_api', async: true, defer: true },
			{
				error: () => {
					VideoComponent.youTubeIframeApiLoading = false;
				}
			}
		);
	}

	static onYouTubeIframeAPIReady() {
		VideoComponent.youTubeIframeApiReady = true;
		let instance;
		while ((instance = VideoComponent.youTubeInitQueue.shift())) {
			instance.initYtPlayer();
		}
	}

	initTCPlayer() {
		if (this.provider !== 'tencent' || !VideoComponent.tcApiReady || this.tcPlayerInstance) {
			return;
		}
		// eslint-disable-next-line no-undef
		this.tcPlayerInstance = new TCPlayer(this.dom.video, {
			fileID: this.dom.video.dataset.fileId,
			appID: this.dom.video.dataset.appId
		});
		this.tcPlayerInstance.on('ready', () => {
			if (this.lazyLoaded) {
				this.showVideoOnly();
			}
			if (this.isIntersecting && this.triggered) {
				this.tcPlayerInstance.play();
			}
		});
	}

	get tcPlayer() {
		this.initTCPlayer();
		return this.tcPlayerInstance;
	}

	initYtPlayer() {
		if (
			this.provider !== 'youtube' ||
			!VideoComponent.youTubeIframeApiReady ||
			this.ytPlayerInstanceInitialized
		) {
			return;
		}
		// eslint-disable-next-line no-new, no-undef
		new YT.Player(this.dom.iframe, {
			events: {
				onReady: event => {
					this.ytPlayerInstance = event.target;
					if (this.isIntersecting && this.triggered) {
						this.ytPlayerInstance.playVideo();
					}
				}
			}
		});
		this.ytPlayerInstanceInitialized = true;
	}

	get ytPlayer() {
		this.initYtPlayer();
		return this.ytPlayerInstance;
	}

	get glueFrame() {
		if (this.provider !== 'video23') {
			return null;
		}
		if (this.glueFrameInstance) {
			return this.glueFrameInstance;
		}
		if (!this.iframeLoaded) {
			return null;
		}
		this.glueFrameInstance = new GlueFrame(this.dom.iframe, 'Player');
		return this.glueFrameInstance;
	}

	loadRssUrl() {
		fetch(this.rssUrl)
			.then(response => response.json())
			.then(data => {
				const { videoId, posterUrl, timestamp, outputDateTimeFormat, title } = data.items[0];
				this.dom.iframe.src = this.dom.iframe.dataset.src + videoId;
				this.dom.posterImage.src = posterUrl;
				const date = DateFormatter.format(
					new Date(timestamp.replace(' ', 'T')),
					outputDateTimeFormat
				);
				this.dom.description.innerHTML = `${date}<br/>${title}`;
				this.dom.container.classList.remove(classes.loading);
				showElement(this.dom.poster);
				showElement(this.dom.play);
			})
			.catch(e => {
				console.error(e);
			});
	}

	onPlayClick(e) {
		e.preventDefault();
		if (this.triggered) {
			return;
		}
		this.triggered = true;
		if (this.lazyLoaded) {
			this.showLoadingIndicator();
			if (this.provider === 'tencent') {
				VideoComponent.loadTCPlayer(this);
			} else {
				this.dom.iframe.src = this.dom.iframe.dataset.src;
				if (this.provider === 'youtube') {
					VideoComponent.loadYouTubeIframeApi(this);
				}
			}
		} else {
			this.play();
		}
	}

	onIframeLoad() {
		this.iframeLoaded = true;
		if (!this.lazyLoaded) {
			return;
		}
		switch (this.provider) {
			case 'video23':
				this.glueFrame.bind('player:loaded', () => {
					this.showVideoOnly();
					if (!this.isIntersecting) {
						this.glueFrame.set('playing', false);
					}
				});
				break;
			case 'youtube':
				this.showVideoOnly();
				break;
			// no default
		}
	}

	addEventListeners() {
		this.addEventListener(this.dom.play, 'click.play', this.onPlayClick.bind(this));
		this.attachObserver();
		if (this.dom.iframe) {
			this.addEventListener(this.dom.iframe, 'load.iframe', this.onIframeLoad.bind(this));
		}
	}

	removeEventListeners() {
		this.removeEventListener(this.dom.play, 'click.play');
		this.detachObserver();
		if (this.dom.iframe) {
			this.removeEventListener(this.dom.iframe, 'load.iframe');
		}
	}

	showLoadingIndicator() {
		this.dom.container.classList.remove(classes.loaded);
		this.dom.container.classList.add(classes.loading);
	}

	showVideoOnly() {
		this.dom.container.classList.add(classes.loaded);
		this.dom.container.classList.remove(classes.loading);
	}

	play() {
		this.showVideoOnly();
		switch (this.provider) {
			case 'video23':
				if (!this.glueFrame) {
					this.lazyLoaded = true;
					return;
				}
				this.glueFrame.set('playing', true);
				// eslint-disable-next-line no-underscore-dangle
				window._gluePlayers = window._gluePlayers || [];
				// eslint-disable-next-line no-underscore-dangle
				window._gluePlayers.push(this.video23Player);
				this.glueFrameInstance.set('playing', true);
				this.dom.iframe.setAttribute('data-twentythree-adobe', 'yes');
				break;
			case 'youtube':
				if (!this.ytPlayer) {
					this.lazyLoaded = true;
					return;
				}
				this.ytPlayer.playVideo();
				break;
			case 'tencent':
				if (!this.tcPlayer) {
					return;
				}
				this.tcPlayer.play();
				break;
			// no default
		}
	}

	cleanup() {
		this.removeEventListeners();
		if (this.glueFrameInstance) {
			this.glueFrameInstance.destroy();
		}
	}

	attachObserver() {
		if (observer) {
			observer.observe(this.dom.iframe || this.dom.video);
		} else {
			observerQueue.push(this);
		}
	}

	detachObserver() {
		if (observer) {
			observer.unobserve(this.dom.iframe || this.dom.video);
		}
	}

	onIntersection(entry) {
		this.isIntersecting = entry.isIntersecting;
		if (this.glueFrame) {
			this.glueFrame.get('playing', playing => {
				if (playing) {
					this.glueFrame.set('playing', false);
				}
			});
		} else if (this.ytPlayer) {
			this.ytPlayer.pauseVideo();
		} else if (this.tcPlayer) {
			this.tcPlayer.pause();
		}
	}

	static onIntersection(entries) {
		entries.forEach(entry => {
			const targetInstance = findInstance(entry.target);
			if (targetInstance) {
				targetInstance.onIntersection(entry);
			}
		});
	}
}

import('intersection-observer').then(() => {
	observer = new IntersectionObserver(VideoComponent.onIntersection);
	let videoComponent;
	while ((videoComponent = observerQueue.pop())) {
		videoComponent.attachObserver();
	}
});
